// vim: set ts=2 sts=2 sw=2 et:

import React from "react";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";

import type { GlobalState } from "../../../types/stateTypes";
import SpectatorContainer from "../../spectator/SpectatorContainer";
import AthleteCard from "../../streaming/AthleteCard";
import BroadcastOverlay from "../../streaming/BroadcastOverlay";
import StreamingLiveClient from "../../streaming/StreamingLiveClient";
import { buildLabLiveState, installLabWebSocket, publishLabState } from "../labLiveSocket";
import { LAB_SCENARIOS, LAB_TOURNAMENT } from "../labScenarios";
import { LAB_SPONSORS } from "../labSponsors";
import { useLabBody } from "../LabStage";

import styles from "../Lab.module.scss";

const PLATFORM_REF = { day: LAB_TOURNAMENT.day, platformId: LAB_TOURNAMENT.platformId };

type Screen = "espectador" | "overlay" | "ficha";

const SCREENS: ReadonlyArray<{ id: Screen; label: string }> = [
  { id: "espectador", label: "Pantalla de espectador" },
  { id: "overlay", label: "Overlay de transmisión" },
  { id: "ficha", label: "Ficha del atleta" },
];

// Monta las pantallas reales (las mismas que van a la tarima y a OBS) conectadas a una sala simulada que
// publica las escenas del lab como lo haría el operador.
const EnVivoLabRoute = () => {
  useLabBody();
  const location = useLocation();
  const initial = React.useMemo(() => new URLSearchParams(location.search), [location.search]);
  const meet = useSelector((state: GlobalState) => state.meet);

  const [ready, setReady] = React.useState(false);
  const [scenarioId, setScenarioId] = React.useState(() => {
    const requested = initial.get("escena");
    return LAB_SCENARIOS.some((scenario) => scenario.id === requested) ? (requested as string) : LAB_SCENARIOS[0].id;
  });
  const [controlsVisible, setControlsVisible] = React.useState(initial.get("controles") !== "0");
  const [bundleSponsors, setBundleSponsors] = React.useState(initial.get("sponsors") !== "imagen");
  const [screen, setScreen] = React.useState<Screen>(() => {
    const requested = initial.get("pantalla");
    return SCREENS.some((option) => option.id === requested) ? (requested as Screen) : "espectador";
  });

  const index = Math.max(
    0,
    LAB_SCENARIOS.findIndex((scenario) => scenario.id === scenarioId),
  );
  const scenario = LAB_SCENARIOS[index];
  // El siguiente atleta es el de la escena siguiente que tenga intento (otra persona, como en la tarima).
  const nextAttempt =
    LAB_SCENARIOS.slice(index + 1)
      .concat(LAB_SCENARIOS.slice(0, index))
      .find((candidate) => candidate.attempt !== null && candidate.attempt.entryId !== scenario.attempt?.entryId)
      ?.attempt ?? null;

  const step = React.useCallback((delta: number) => {
    setScenarioId((current) => {
      const position = LAB_SCENARIOS.findIndex((candidate) => candidate.id === current);
      return LAB_SCENARIOS[(position + delta + LAB_SCENARIOS.length) % LAB_SCENARIOS.length].id;
    });
  }, []);

  // El socket simulado tiene que estar instalado antes de que la pantalla se monte y se conecte.
  React.useLayoutEffect(() => {
    const restore = installLabWebSocket();
    setReady(true);
    return restore;
  }, []);

  React.useEffect(() => {
    if (!ready) return;
    const broadcast = bundleSponsors
      ? { meetName: meet.name || LAB_TOURNAMENT.name, sponsors: [...LAB_SPONSORS] }
      : undefined;
    publishLabState(buildLabLiveState(scenario, nextAttempt, meet, Date.now(), broadcast));
  }, [bundleSponsors, meet, nextAttempt, ready, scenario]);

  React.useEffect(() => {
    const params = new URLSearchParams({ pantalla: screen, escena: scenarioId });
    if (!bundleSponsors) params.set("sponsors", "imagen");
    if (!controlsVisible) params.set("controles", "0");
    window.history.replaceState(null, "", `${location.pathname}?${params.toString()}`);
  }, [bundleSponsors, controlsVisible, location.pathname, scenarioId, screen]);

  React.useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLSelectElement || event.target instanceof HTMLInputElement) return;
      if (event.key === "h" || event.key === "H") setControlsVisible((visible) => !visible);
      if (event.key === "ArrowRight") step(1);
      if (event.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [step]);

  return (
    <>
      {!ready ? null : screen === "espectador" ? (
        <SpectatorContainer key="espectador" platformRef={PLATFORM_REF} locationSearch="?ws=lab" />
      ) : (
        <StreamingLiveClient
          key={screen}
          platformRef={PLATFORM_REF}
          locationSearch="?ws=lab"
          followPolicy={screen === "overlay" ? "result-reveal" : "immediate-next"}
        >
          {(state) => (screen === "overlay" ? <BroadcastOverlay {...state} /> : <AthleteCard {...state} />)}
        </StreamingLiveClient>
      )}

      {controlsVisible ? (
        <aside className={styles.controls} aria-label="Controles del lab">
          <div className={styles.controlsTitle}>
            <h1>Pantallas en vivo</h1>
            <Link to="/lab">Lab</Link>
          </div>
          <label>
            Pantalla
            <select value={screen} onChange={(event) => setScreen(event.target.value as Screen)}>
              {SCREENS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Escena
            <select value={scenario.id} onChange={(event) => setScenarioId(event.target.value)}>
              {LAB_SCENARIOS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Sponsors
            <select
              value={bundleSponsors ? "bundle" : "imagen"}
              onChange={(event) => setBundleSponsors(event.target.value === "bundle")}
            >
              <option value="bundle">Del bundle (los de la web)</option>
              <option value="imagen">Imagen fija (sin sponsors en el bundle)</option>
            </select>
          </label>
          <div className={styles.controlsRow}>
            <button type="button" onClick={() => step(-1)}>
              Anterior
            </button>
            <button type="button" onClick={() => step(1)}>
              Siguiente
            </button>
          </div>
          <p className={styles.controlsHint}>
            Son las pantallas de producción conectadas a una sala simulada. H oculta los controles y ← → cambian de
            escena.
          </p>
        </aside>
      ) : null}
    </>
  );
};

export default EnVivoLabRoute;
